import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import {
  CustomerChoicePayload,
  CustomerMappingsPayload,
  CustomFieldChoicePayload,
  CustomFieldMappingsPayload,
  ProductChoicePayload,
  ProductMappingsPayload,
  QuickbooksStateTypes,
  UrlParams,
  VendorParams,
} from '../../dashboard/components/Quickbooks/types';
import { axiosGetParallelData, axiosPostData } from '../../helpers/axios';

export const getQuickbooksData = createAsyncThunk(
  'getQuickbooksData',
  async ({ vendorId, onError }: VendorParams, { rejectWithValue }) =>
    axiosGetParallelData({
      getParams: {
        urls: [
          `/integrations/quickbooks-online/vendor/${vendorId}/lt-customers`,
          `/integrations/quickbooks-online/vendor/${vendorId}/lt-products`,
          `/integrations/quickbooks-online/vendor/${vendorId}/qbo-items`,
          `/integrations/quickbooks-online/vendor/${vendorId}/qbo-salesform-custom-fields`,
          `/integrations/quickbooks-online/vendor/${vendorId}/qbo-customers`,
        ],
        keys: [
          'quickbooksOnlineLTCustomers',
          'quickbooksOnlineProducts',
          'quickbooksOnlineItems',
          'quickbooksOnlineSalesformCustomFields',
          'quickbooksOnlineQBOCustomers',
        ],
      },
      onError,
      rejectWithValue,
    })
);

export const handleCustomerChoiceChange = createAction<CustomerChoicePayload>(
  'handleCustomerChoiceChange'
);

export const handleCustomFieldChoiceChange =
  createAction<CustomFieldChoicePayload>('handleCustomFieldChoiceChange');

export const handleProductChoiceChange = createAction<ProductChoicePayload>(
  'handleProductChoiceChange'
);

export const postCustomerMappings = createAsyncThunk(
  'postCustomerMappings',
  async (
    { customers, onError, onSuccess, vendorId }: CustomerMappingsPayload,
    { rejectWithValue }
  ) =>
    axiosPostData({
      url: `/integrations/quickbooks-online/vendor/${vendorId}/customer-mappings`,
      data: { customers },
      onError,
      onSuccess,
      rejectWithValue,
    })
);

export const postCustomFieldMappings = createAsyncThunk(
  'postCustomFieldMappings',
  async (
    { customFields, onError, onSuccess, vendorId }: CustomFieldMappingsPayload,
    { rejectWithValue }
  ) =>
    axiosPostData({
      url: `/integrations/quickbooks-online/vendor/${vendorId}/salesform-custom-field-values`,
      data: { customFields },
      onError,
      onSuccess,
      rejectWithValue,
    })
);

export const postProductMappings = createAsyncThunk(
  'postProductMappings',
  async (
    { onError, onSuccess, products, vendorId }: ProductMappingsPayload,
    { rejectWithValue }
  ) =>
    axiosPostData({
      url: `/integrations/quickbooks-online/vendor/${vendorId}/product-mappings`,
      data: { products },
      onError,
      onSuccess,
      rejectWithValue,
    })
);

export const refreshQuickbooksIntegration = createAsyncThunk(
  'refreshQuickbooksIntegration',
  async ({ onError, onSuccess, url }: UrlParams, { rejectWithValue }) =>
    axiosPostData({
      onError,
      onSuccess,
      rejectWithValue,
      url,
    })
);

export const resetCustomerMappings = createAction('resetCustomerMappings');

export const resetCustomFieldMappings = createAction(
  'resetCustomFieldMappings'
);

export const resetProductMappings = createAction('resetProductMappings');

export const revokeQuickbooksIntegration = createAsyncThunk(
  'revokeQuickbooksIntegration',
  async ({ onError, onSuccess, url }: UrlParams, { rejectWithValue }) =>
    axiosPostData({
      onError,
      onSuccess,
      rejectWithValue,
      url,
    })
);

export const setActiveTab = createAction<string>('setActiveTab');

export const setInitialQuickbooksData = createAction<QuickbooksStateTypes>(
  'setInitialQuickbooksData'
);

export const setTableRowsPerPage = createAction<number>('setTableRowsPerPage');
